import { MoreVertical, MoveRight, RotateCcw, Trash2 } from "lucide-react";
import React from "react";
import { useCodiffLinks } from "../hooks/useCodiffLinks";
import { usePopoverMenu } from "../hooks/usePopoverMenu";
import { formatProjectName, mrLinkUrl } from "../utils";
import { ApprovalStatus } from "./ApprovalStatus";
import { AltLinkButton } from "./AltLinkButton";
import { Avatar } from "./Avatar";
import { DateTime } from "./DateTime";
import { MRComments } from "./MRComments";
import { CopyLinkButton } from "./CopyLinkButton";
import { DraftIcon, MRStatusIcons } from "./MRStatusIcons";
import { JiraBadge } from "./JiraBadge";
import { PipelineStatus } from "./PipelineStatus";
import { StackChain } from "./StackChain";
import { StackIndicator } from "./StackIndicator";
import { Tooltip } from "./Tooltip";

export const SwimlaneCard = ({
	mr,
	onDragStart,
	onDragEnd,
	onDropTop,
	onDropBottom,
	onDragOverTop,
	onDragOverBottom,
	showIndicator,
	isDragging,
	onDelete,
	onRestore,
	onMoveColumn,
	moveColumns = [],
	onMoveSwimlane,
	moveSwimlanes = [],
	laneId,
	enableCommentsExpansion = false,
}) => {
	const cardRef = React.useRef(null);
	const [showMenu, setShowMenu] = React.useState(false);
	const { triggerRef, menuRef, closeMenu } = usePopoverMenu(
		showMenu,
		setShowMenu,
	);
	const [useCodiff] = useCodiffLinks();

	if (!mr) return null;

	const isClosed = mr.state === "merged" || mr.state === "closed";

	const handleDragStart = (e) => {
		e.dataTransfer.effectAllowed = "move";
		e.dataTransfer.setData("text/plain", mr.id);

		// Create a custom drag image with opacity
		if (cardRef.current) {
			const clone = cardRef.current.cloneNode(true);
			clone.style.opacity = "0.5";
			clone.style.position = "absolute";
			clone.style.top = "-9999px";
			document.body.appendChild(clone);

			e.dataTransfer.setDragImage(clone, e.offsetX, e.offsetY);

			// Clean up the clone after drag starts
			setTimeout(() => {
				document.body.removeChild(clone);
			}, 0);
		}

		if (onDragStart) {
			onDragStart(mr);
		}
	};

	const handleDragEnd = () => {
		if (onDragEnd) {
			onDragEnd();
		}
	};

	return (
		<li className="relative list-none">
			<div
				ref={cardRef}
				data-mr-card={mr.id}
				draggable={!!onDragStart}
				onDragStart={handleDragStart}
				onDragEnd={handleDragEnd}
				className={`rounded-lg p-3 my-2 border border-[var(--color-border)] hover:border-[var(--color-border-hover)] transition-colors relative ${
					onDragStart ? "cursor-grab active:cursor-grabbing" : "cursor-default"
				} ${mr.stack?.isBlockedByStack ? "ml-3 opacity-60 hover:opacity-100" : ""}`}
			>
				{onDropTop && isDragging && (
					<div
						className="absolute z-10"
						style={{
							top: "-0.5rem",
							left: "-1rem",
							right: "-1rem",
							height: "calc(50% + 0.5rem)",
						}}
						onDragOver={(e) => {
							e.preventDefault();
							e.stopPropagation();
							if (onDragOverTop) onDragOverTop();
						}}
						onDrop={onDropTop}
					>
						{showIndicator && (
							<div className="h-1 bg-[var(--color-accent)] rounded-full absolute top-0 left-4 right-4" />
						)}
					</div>
				)}
				{onDropBottom && isDragging && (
					<div
						className="absolute z-10"
						style={{
							bottom: "-0.5rem",
							left: "-1rem",
							right: "-1rem",
							height: "calc(50% + 0.5rem)",
						}}
						onDragOver={(e) => {
							e.preventDefault();
							e.stopPropagation();
							if (onDragOverBottom) onDragOverBottom();
						}}
						onDrop={onDropBottom}
					/>
				)}
				{(onDelete || onRestore || onMoveColumn || onMoveSwimlane) && (
					<div className="absolute top-1 right-1 z-20" ref={menuRef}>
						<button
							ref={triggerRef}
							type="button"
							onClick={(e) => {
								e.stopPropagation();
								setShowMenu(!showMenu);
							}}
							className="p-1 hover:bg-[var(--color-bg-secondary)] rounded-full transition-colors"
							aria-label="More options"
							aria-haspopup="menu"
							aria-expanded={showMenu}
						>
							<MoreVertical className="w-4 h-4 text-[var(--color-text-secondary)]" />
						</button>
						{showMenu && (
							<div
								role="menu"
								className="absolute right-0 mt-1 bg-[var(--color-bg-primary)] border border-[var(--color-border)] rounded-lg shadow-lg py-1 z-20 min-w-[180px] max-h-72 overflow-y-auto"
							>
								{laneId === "deleted" && onRestore ? (
									<button
										type="button"
										role="menuitem"
										onClick={(e) => {
											e.stopPropagation();
											onRestore(mr.id);
											closeMenu();
										}}
										className="w-full px-3 py-2 text-left text-sm hover:bg-[var(--color-bg-secondary)] flex items-center gap-2 text-[var(--color-accent)]"
									>
										<RotateCcw className="w-4 h-4" />
										Restore
									</button>
								) : (
									<>
										{onMoveSwimlane && moveSwimlanes.length > 0 && (
											<>
												<div className="px-3 pb-1 pt-2 text-xs font-medium uppercase tracking-wide text-[var(--color-text-secondary)]">
													Work stream
												</div>
												{moveSwimlanes.map((swimlane) => (
													<button
														key={swimlane.id}
														type="button"
														role="menuitem"
														onClick={(e) => {
															e.stopPropagation();
															onMoveSwimlane(mr.id, swimlane.id);
															closeMenu();
														}}
														className="w-full px-3 py-2 text-left text-sm hover:bg-[var(--color-bg-secondary)] flex items-center gap-2 text-[var(--color-text-primary)]"
													>
														<MoveRight className="w-4 h-4 text-[var(--color-text-secondary)]" />
														<span className="truncate">{swimlane.title}</span>
													</button>
												))}
											</>
										)}
										{onMoveColumn && moveColumns.length > 0 && (
											<>
												<div className="px-3 pb-1 pt-2 text-xs font-medium uppercase tracking-wide text-[var(--color-text-secondary)]">
													Workflow column
												</div>
												{moveColumns.map((column) => (
													<button
														key={column.id}
														type="button"
														role="menuitem"
														onClick={(e) => {
															e.stopPropagation();
															onMoveColumn(mr.id, column.id);
															closeMenu();
														}}
														className="w-full px-3 py-2 text-left text-sm hover:bg-[var(--color-bg-secondary)] flex items-center gap-2 text-[var(--color-text-primary)]"
													>
														<MoveRight className="w-4 h-4 text-[var(--color-text-secondary)]" />
														<span className="truncate">{column.title}</span>
													</button>
												))}
											</>
										)}
										{onDelete && (
											<button
												type="button"
												role="menuitem"
												onClick={(e) => {
													e.stopPropagation();
													onDelete(mr.id);
													closeMenu();
												}}
												className="w-full px-3 py-2 text-left text-sm hover:bg-[var(--color-bg-secondary)] flex items-center gap-2 text-red-500"
											>
												<Trash2 className="w-4 h-4" />
												Delete
											</button>
										)}
									</>
								)}
							</div>
						)}
					</div>
				)}
				<div className="flex gap-3">
					<Tooltip content={mr.author?.name || "Unknown Author"}>
						<Avatar
							src={mr.author?.avatarUrl}
							alt={mr.author?.name}
							size="md"
						/>
					</Tooltip>
					<div className="flex-1 min-w-0">
						<div className="flex items-start justify-between gap-2 mb-2">
							<a
								href={mrLinkUrl(mr.webUrl, useCodiff)}
								target="_blank"
								rel="noopener noreferrer"
								draggable="false"
								className="text-sm font-semibold text-[var(--color-link)] hover:underline break-words line-clamp-2 flex-1 pr-4"
							>
								{mr.title}
							</a>
						</div>

						<div className="flex items-center justify-between gap-2 mb-2">
							<div className="flex gap-2 items-center flex-wrap">
								<a
									href={mr.webUrl?.split("/-/")[0]}
									target="_blank"
									rel="noopener noreferrer"
									draggable="false"
									className="text-xs text-[var(--color-text-secondary)] hover:underline"
								>
									{formatProjectName(mr.projectPath)}
								</a>
								<JiraBadge mr={mr} />
								<MRStatusIcons mr={mr} iconClass="w-4 h-4" />
							</div>
							<div className="flex flex-col items-end gap-1 flex-shrink-0">
								<div className="flex gap-2 items-center">
									{!isClosed && (
										<>
											<PipelineStatus pipeline={mr.headPipeline} />
											<ApprovalStatus
												aiReviewMissing={mr.aiReviewMissing}
												approvers={mr.approvers}
												reviewers={mr.reviewers}
												approvalsLeft={mr.approvalsLeft}
											/>
										</>
									)}
									<DraftIcon draft={mr.draft} iconClass="w-4 h-4" />
									<AltLinkButton mr={mr} />
									<CopyLinkButton mr={mr} />
								</div>
								<StackIndicator mr={mr} compact />
							</div>
						</div>

						<div className="flex items-center gap-x-2 gap-y-1 text-xs text-[var(--color-text-secondary)]">
							{mr.createdAt && (
								<span>
									Created <DateTime date={mr.createdAt} />
								</span>
							)}
							{mr.updatedAt && (
								<span>
									• Updated <DateTime date={mr.updatedAt} />
								</span>
							)}
						</div>
					</div>
				</div>

				<StackChain mr={mr} className="mt-2" />

				{enableCommentsExpansion && (
					<MRComments
						mrId={mr.id}
						className="mt-2 pt-2 border-t border-[var(--color-border)]"
					/>
				)}
			</div>
		</li>
	);
};
